import { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getDealsColumns } from "./getDealsColumns";
import {
  REASON_MODAL_CONFIG,
  DEAL_APPROVAL_SUCCESS_MESSAGES,
  getDealApprovalSuccessMessage,
} from "../../../constants/constants";
import {
  Rejectdeal,
  dealsView,
  Cancledeal,
  getExpiryDays,
  Republishdeal,
  sendaggrement,
  sendAgreementReminder,
  getAggrementStatus,
  sendOverdueReminder,
  sendRepaymentReminder,
} from "../../../redux/slices/dealsSlice";
import CustomTable from "../../../components/CustomTable/CustomTable";
import CustomModal from "../../../components/CustomModal/CustomModal";
import ReasonForm from "../../../components/CustomForms/ReasonForm";
import { App } from "antd";
import DisburseForm from "./DisburseForm";
import { count } from "../../../redux/slices/dealsSlice";
import { count as allCount } from "../../../redux/slices/AuthSlice";
import {
  TABLE_COLUMN_DEFAULTS,
  buildTableScreenKey,
} from "../../../constants/tableColumnDefaults";

const DealsTable = ({
  tableData,
  selectedStatus,
  pagination,
  dealCategoryId = "DCT1000001",
  onColumnControlChange,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { user } = useSelector((state) => state.auth);
  const { loading, cancelLoading, cancelError, expiryDays, sendAgreeloading } =
    useSelector((state) => state.deals);
  const [modal, setModal] = useState({ type: null, data: null });
  const [reminderLoadingMap, setReminderLoadingMap] = useState({});

  const userPrivileges = useMemo(
    () => user?.privilege?.map((p) => p.privilegeCode) || [],
    [user],
  );

  const closeModal = useCallback(
    () => setModal({ type: null, data: null }),
    [],
  );

  const openModal = useCallback(
    (type, deal) => {
      setModal({ type, data: deal });
      if (type === "republish") {
        dispatch(getExpiryDays());
      } else if (type === "disburse") {
        dispatch(getAggrementStatus(deal.dealId));
      }
    },
    [dispatch],
  );

  const handleReasonSubmit = useCallback(
    async (values) => {
      if (!modal.type || !modal.data?.dealId) return;
      let action;
      const payload = {
        dealId: modal.data?.dealId,
        comments: values.reason || "",
        modifiedAt: modal.data?.modifiedAt,
      };

      switch (modal.type) {
        case "reject":
          action = Rejectdeal;
          break;
        case "cancel":
          action = Cancledeal;
          break;
        case "agreement":
          action = sendaggrement;
          break;
        case "republish":
          action = Republishdeal;
          payload.expiryDays = Number(values.expiryDays);
          break;
        default:
          break;
      }

      if (action) {
        const res = await dispatch(action(payload));
        if (res.payload?.statusCode === 201) {
          const dealId = modal.data?.dealId;
          if (modal.type === "reject") {
            message.success(
              getDealApprovalSuccessMessage(
                DEAL_APPROVAL_SUCCESS_MESSAGES.DEAL_REJECTED,
                dealId,
              ),
            );
          } else if (modal.type === "cancel") {
            message.success(
              getDealApprovalSuccessMessage(
                DEAL_APPROVAL_SUCCESS_MESSAGES.DEAL_CANCELLED,
                dealId,
              ),
            );
          }
          dispatch(dealsView({ selectedStatus, dealCategoryId }));
          dispatch(count("DEALS"));
          dispatch(allCount("ALL"));
          closeModal();
        }
      }
    },
    [
      modal.type,
      modal.data?.dealId,
      modal.data?.modifiedAt,
      dispatch,
      selectedStatus,
      dealCategoryId,
      closeModal,
      message,
    ],
  );

  const handleView = useCallback(
    (id) =>
      navigate("/deals/deal-details", {
        state: { dealId: id, selectedStatus, dealCategoryId },
      }),
    [navigate, selectedStatus, dealCategoryId],
  );

  const sendAgreementMail = useCallback(
    async (dealId) => {
      setReminderLoadingMap((prev) => ({ ...prev, [dealId]: true }));
      const payload = {
        dealId,
      };
      try {
        const response = await dispatch(sendAgreementReminder(payload));
        if (response?.payload?.statusCode === 200) {
          message.success("Agreement Reminder email sent successfully!");
        } else {
          message.error("Failed to send Agreement Reminder email.");
        }
      } finally {
        // reset loading for that dealId
        setReminderLoadingMap((prev) => ({ ...prev, [dealId]: false }));
      }
    },
    [dispatch, message],
  );

  const sendRemainderMail = useCallback(
    async (dealId, type) => {
      const payload = { dealId };
      let action =
        type === "repayment" ? sendRepaymentReminder : sendOverdueReminder;

      // set loading true only for that dealId
      setReminderLoadingMap((prev) => ({ ...prev, [dealId]: true }));

      try {
        const response = await dispatch(action(payload));
        if (response?.payload?.statusCode === 200) {
          message.success("Reminder email sent successfully!");
        } else {
          message.error("Failed to send Reminder email.");
        }
      } finally {
        // reset loading for that dealId
        setReminderLoadingMap((prev) => ({ ...prev, [dealId]: false }));
      }
    },
    [dispatch, message],
  );

  // 🔹 Columns for Deals Table
  const columns = useMemo(
    () =>
      getDealsColumns({
        status: selectedStatus,
        dealCategoryId,
        privileges: userPrivileges,
        sendAgreeloading: sendAgreeloading,
        loadingDeals: reminderLoadingMap,
        currentUser: user,
        onView: (deal) => handleView(deal.dealId),
        onAssign: (deal) => navigate(`/deals/approve-deal/${deal?.dealId}`),
        onReject: (deal) => openModal("reject", deal),
        onCancel: (deal) => openModal("cancel", deal),
        onSendTOAgree: (deal) => openModal("agreement", deal),
        onDisburse: (deal) => openModal("disburse", deal),
        onSendEmail: (deal) => sendAgreementMail(deal.dealId),
        onRepublish: (deal) => openModal("republish", deal),
        onRepaymentReminder: (deal) =>
          sendRemainderMail(deal.dealId, "repayment"),
        onOverdueReminder: (deal) => sendRemainderMail(deal.dealId, "overdue"),
      }),
    [
      selectedStatus,
      dealCategoryId,
      userPrivileges,
      sendAgreeloading,
      reminderLoadingMap,
      handleView,
      navigate,
      openModal,
      sendAgreementMail,
      sendRemainderMail,
      user,
    ],
  );

  const screenKey = useMemo(
    () => buildTableScreenKey("deals", dealCategoryId),
    [dealCategoryId],
  );

  return (
    <>
      <CustomTable
        rowKey="dealId"
        columns={columns}
        dataSource={tableData}
        loading={loading}
        pagination={pagination}
        screenKey={screenKey}
        defaultColumnKeys={TABLE_COLUMN_DEFAULTS.deals}
        onColumnControlChange={onColumnControlChange}
      />

      {modal.type && (
        <CustomModal
          title={`${
            modal.type.charAt(0).toUpperCase() + modal.type.slice(1)
          } Deal`}
          open
          centered
          content={
            <>
              {modal.type === "disburse" ? (
                <DisburseForm
                  onClose={closeModal}
                  dealId={modal.data?.dealId}
                  status={selectedStatus}
                  dealCategoryId={dealCategoryId}
                />
              ) : (
                <ReasonForm
                  label={REASON_MODAL_CONFIG[modal.type]?.label}
                  placeholder={
                    modal.type === "republish"
                      ? "Enter Comments"
                      : "Write a valid reason"
                  }
                  btnName={REASON_MODAL_CONFIG[modal.type]?.btnName}
                  btnType={
                    modal.type === "reject" || modal.type === "cancel"
                      ? "danger"
                      : "primary"
                  }
                  submitLoading={cancelLoading}
                  submitError={cancelError}
                  onClose={closeModal}
                  handleOnFormSubmit={handleReasonSubmit}
                  hasSelect={modal.type === "republish" && true}
                  options={expiryDays?.map((day) => ({
                    value: day,
                    label: day,
                  }))}
                />
              )}
            </>
          }
        />
      )}
    </>
  );
};

export default DealsTable;
